/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { getPhase } from "../data/constants";
import { fmtMoney, fmtMoneyShort, projectProgress, projectCollected } from "../lib/appUtils";
import { TODAY } from "../lib/schedule";
import ProgressRing from "../components/ProgressRing";
import TargetDialog from "../components/TargetDialog";
import { PullToRefresh } from "../components/PullToRefresh";
import AvatarCropDialog from "../components/AvatarCropDialog";
import { uploadChunked } from "../lib/upload";
import { avatarSrc } from "../lib/chat";
import { Building2, DollarSign, CalendarClock, Wallet, TrendingUp, AlertCircle, FileSignature, Target, Camera } from "lucide-react";

function inThisMonth(dstr) {
  if (!dstr) return false;
  const d = new Date(dstr);
  return d.getFullYear() === TODAY.getFullYear() && d.getMonth() === TODAY.getMonth();
}

export default function SalesDashboard({ embedded }) {
  const { lang, visibleProjects, user, salesTargets, updateAvatar, refresh, api } = useApp();
  const nav = useNavigate();
  const [editTarget, setEditTarget] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const fileRef = useRef(null);
  const allProjects = visibleProjects; // backend already scoped to this salesperson
  const projects = allProjects.filter((p) => p.phaseIndex < 7); // hide completed from lists

  const onPickAvatar = (e) => { const file = e.target.files?.[0]; e.target.value = ""; if (file) setCropFile(file); };
  const onCropped = async (blob) => {
    setUploading(true);
    try { const r = await uploadChunked(api, blob, { name: "avatar.jpg", type: "image/jpeg" }); await updateAvatar({ fileId: r.fileId }); setCropFile(null); }
    catch { alert(lang === "zh" ? "头像上传失败" : "Avatar upload failed"); }
    setUploading(false);
  };

  const inProgress = projects.filter((p) => p.phaseIndex >= 0 && p.phaseIndex < 7).length;
  const totalSigned = allProjects.reduce((s, p) => s + (p.contractAmount || 0), 0);
  const monthSigned = allProjects.filter((p) => inThisMonth(p.signedDate)).reduce((s, p) => s + (p.contractAmount || 0), 0);
  const monthCollected = allProjects.reduce((s, p) => s + (p.payments || []).filter((x) => x.status === "paid" && inThisMonth(x.paidAt || p.signedDate)).reduce((a, x) => a + (x.paidAmount || x.amount || 0), 0), 0);
  const collected = allProjects.reduce((s, p) => s + projectCollected(p), 0);
  const outstandingList = projects.filter((p) => p.payments.some((x) => x.status === "pending_invoice" || x.status === "invoiced"));
  const toCollect = projects.reduce((s, p) => s + p.payments.filter((x) => x.status === "pending_invoice" || x.status === "invoiced").reduce((a, x) => a + x.amount, 0), 0);
  const unpaidInvoiced = projects.reduce((s, p) => s + p.payments.filter((x) => x.status === "invoiced").reduce((a, x) => a + x.amount, 0), 0);
  const tg = salesTargets[user.salesName] || {};
  const behindMonth = (tg.monthSign > 0 && monthSigned < tg.monthSign) || (tg.monthCollect > 0 && monthCollected < tg.monthCollect);

  const Stat = ({ icon: Icon, label, value, sub, tint }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex-wrap md:flex-nowrap flex items-center justify-between"><p className="text-sm text-slate-500 font-medium">{label}</p>
        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: tint + "22" }}><Icon className="h-5 w-5" style={{ color: tint }} /></div></div>
      <p className="text-2xl font-extrabold text-slate-900 mt-3">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );

  return (
    <PullToRefresh onRefresh={refresh} zh={lang === "zh"}>
    <div className="space-y-6">
      <div className="flex-wrap md:flex-nowrap flex items-center gap-4">
        <div className="relative group">
          <button data-testid="avatar-upload-btn" onClick={() => fileRef.current?.click()} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 relative h-16 w-16 rounded-full overflow-hidden border-2 border-brand-200 bg-slate-100 flex items-center justify-center hover:border-brand-400 transition-colors">
            {user.avatar ? (
              <img src={avatarSrc(user.avatar)} alt="avatar" data-testid="avatar-img" className="h-full w-full object-cover" />
            ) : (
              <span className="text-xl font-bold text-red-600">{(user.name || "?").charAt(0)}</span>
            )}
            <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              <Camera className="h-5 w-5 text-white" />
            </span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onPickAvatar} className="hidden" data-testid="avatar-file-input" />
          <AvatarCropDialog file={cropFile} zh={lang === "zh"} busy={uploading} onDone={onCropped} onCancel={() => { if (!uploading) setCropFile(null); }} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{lang === "zh" ? "我的销售看板" : "My Sales Dashboard"}</h1>
          <p className="text-slate-500 text-sm">{uploading ? (lang === "zh" ? "头像上传中…" : "Uploading…") : (lang === "zh" ? `销售：${user.name}· 仅显示您签单的项目` : `Salesperson: ${user.name} · your projects only`)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Building2} label={lang === "zh" ? "在建工程项目" : "In-Progress"} value={inProgress} tint="#4F6BFF" />
        <Stat icon={FileSignature} label={lang === "zh" ? "总签单金额" : "Total Signed"} value={fmtMoneyShort(totalSigned)} tint="#0ea5e9" />
        <Stat icon={CalendarClock} label={lang === "zh" ? "本月签单金额" : "Signed This Month"} value={fmtMoneyShort(monthSigned)} tint="#8b5cf6" />
        <Stat icon={Wallet} label={lang === "zh" ? "已收款金额" : "Collected"} value={fmtMoneyShort(collected)} tint="#16a34a" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat icon={TrendingUp} label={lang === "zh" ? "本月需收款金额" : "To Collect (Month)"} value={fmtMoney(toCollect)} sub={`${outstandingList.length} ${lang === "zh" ? "个项目" : "projects"}`} tint="#2563eb" />
        <Stat icon={AlertCircle} label={lang === "zh" ? "应收但未到账" : "Invoiced Unpaid"} value={fmtMoney(unpaidInvoiced)} tint="#d97706" />
        <Stat icon={DollarSign} label={lang === "zh" ? "合同总额" : "Total Contract"} value={fmtMoney(totalSigned)} tint="#0f172a" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="my-targets">
        <div className="flex-wrap md:flex-nowrap flex items-center justify-between mb-4">
          <div className="flex-wrap md:flex-nowrap flex items-center gap-2"><Target className="h-4 w-4 text-red-600" /><h3 className="font-semibold text-slate-900">{lang === "zh" ? "我的目标达成" : "My Target Progress"}</h3></div>
          <button data-testid="set-my-target-btn" onClick={() => setEditTarget(true)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand-500 border border-slate-200 hover:border-brand-300 rounded-lg px-2.5 py-1.5 transition-colors">
            <Target className="h-3.5 w-3.5" />{lang === "zh" ? "设置我的目标" : "Set My Targets"}
          </button>
        </div>
        {behindMonth && (
          <div data-testid="target-warning-banner" className="flex-wrap md:flex-nowrap flex items-center gap-2 rounded-lg bg-red-50 border border-red-100 px-3 py-2 mb-4 text-sm text-red-600">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{lang === "zh" ? "本月签单或回款尚未达标，请加快冲刺月度目标！" : "You are below your monthly sign/collect target — keep pushing!"}</span>
          </div>
        )}
        <div className="flex items-center justify-around flex-wrap gap-4">
          <ProgressRing actual={monthSigned} target={tg.monthSign || 0} color="#4F6BFF" size={76} label={lang === "zh" ? "本月签单" : "Month Sign"} />
          <ProgressRing actual={monthCollected} target={tg.monthCollect || 0} color="#2563eb" size={76} label={lang === "zh" ? "本月回款" : "Month Collect"} />
          <ProgressRing actual={totalSigned} target={tg.yearSign || 0} color="#d97706" size={76} label={lang === "zh" ? "年度签单" : "Annual Sign"} />
          <ProgressRing actual={collected} target={tg.yearCollect || 0} color="#16a34a" size={76} label={lang === "zh" ? "年度回款" : "Annual Collect"} />
        </div>
        {tg.auto && (
          <p className="text-[11px] text-slate-400 mt-3 text-center" data-testid="target-mode-hint">
            {lang === "zh"
              ? `目标来源：签单${tg.auto.monthSign ? `按分公司默认（每月 ${tg.defaults?.dealsPerMonth ?? 0} 单 × ${fmtMoneyShort(tg.defaults?.dealPrice || 0)}，年度 ×12）` : "手动设置"} · 回款${tg.auto.monthCollect ? `按本月需收款项目动态（${tg.defaults?.dueProjects ?? 0} 个项目）` : "手动设置"}`
              : `Targets: sign ${tg.auto.monthSign ? "branch default" : "manual"} · collect ${tg.auto.monthCollect ? "dynamic (projects due)" : "manual"}`}
          </p>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100"><h3 className="font-semibold text-slate-900">{lang === "zh" ? "本月需收款项目" : "Projects Needing Collection This Month"}</h3></div>
        <div className="overflow-x-auto">
          <ResponsiveTable className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500"><tr>
              <th className="text-left font-medium px-5 py-3">{lang === "zh" ? "项目" : "Project"}</th>
              <th className="text-left font-medium px-4 py-3">{lang === "zh" ? "阶段" : "Stage"}</th>
              <th className="text-right font-medium px-4 py-3">{lang === "zh" ? "需收款" : "To Collect"}</th>
              <th className="text-right font-medium px-4 py-3">{lang === "zh" ? "已收" : "Collected"}</th>
            </tr></thead>
            <tbody>
              {outstandingList.length === 0 && <tr><td colSpan={4} className="text-center text-slate-300 py-6">—</td></tr>}
              {outstandingList.map((p) => {
                const ph = getPhase(p.phaseIndex);
                const due = p.payments.filter((x) => x.status === "pending_invoice" || x.status === "invoiced").reduce((a, x) => a + x.amount, 0);
                return (
                  <tr key={p.id} data-testid={`sd-receivable-row-${p.id}`} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => nav(`/project/${p.id}`)}>
                    <td className="px-5 py-3"><p className="font-medium text-slate-800 max-w-[260px] truncate">{p.address}</p><p className="text-[11px] text-slate-400">{p.clientName} · {p.type}</p></td>
                    <td className="px-4 py-3"><span className="text-xs font-medium" style={{ color: ph.color }}>{lang === "zh" ? ph.zh : ph.en}</span></td>
                    <td className="px-4 py-3 text-right font-medium text-blue-600">{fmtMoney(due)}</td>
                    <td className="px-4 py-3 text-right text-green-600">{fmtMoney(projectCollected(p))}</td>
                  </tr>
                );
              })}
            </tbody>
          </ResponsiveTable>
        </div>
      </div>

      {!embedded && (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100"><h3 className="font-semibold text-slate-900">{lang === "zh" ? "我的全部项目" : "All My Projects"} ({projects.length})</h3></div>
        <div className="overflow-x-auto">
          <ResponsiveTable className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500"><tr>
              <th className="text-left font-medium px-5 py-3">{lang === "zh" ? "项目" : "Project"}</th>
              <th className="text-left font-medium px-4 py-3">{lang === "zh" ? "阶段" : "Stage"}</th>
              <th className="text-left font-medium px-4 py-3 w-40">{lang === "zh" ? "施工进度" : "Progress"}</th>
              <th className="text-right font-medium px-4 py-3">{lang === "zh" ? "合同金额" : "Contract"}</th>
            </tr></thead>
            <tbody>
              {projects.map((p) => {
                const ph = getPhase(p.phaseIndex); const prog = projectProgress(p);
                return (
                  <tr key={p.id} data-testid={`sd-in-progress-row-${p.id}`} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => nav(`/project/${p.id}`)}>
                    <td className="px-5 py-3"><p className="font-medium text-slate-800 max-w-[260px] truncate">{p.address}</p><p className="text-[11px] text-slate-400">{p.clientName} · {p.type}</p></td>
                    <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full" style={{ background: ph.color + "22", color: ph.color }}>{lang === "zh" ? ph.zh : ph.en}</span></td>
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${prog}%`, background: ph.color }} /></div><span className="text-xs text-slate-400 w-8">{prog}%</span></div></td>
                    <td className="px-4 py-3 text-right text-slate-600">{fmtMoney(p.contractAmount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </ResponsiveTable>
        </div>
      </div>
      )}

      {editTarget && <TargetDialog salesName={user.salesName} open onClose={() => setEditTarget(false)} />}
    </div>
    </PullToRefresh>
  );
}
